import { useId, useMemo, useState } from 'react';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import type { DiscordRoleOption } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type DiscordRolePickerProps = {
  value: string[];
  onChange: (value: string[]) => void;
  roles?: DiscordRoleOption[];
  isLoading?: boolean;
  isError?: boolean;
  disabled?: boolean;
};

const DISCORD_ROLE_ID = /^\d{17,20}$/;
const MAX_DISCORD_ROLES = 25;

export function DiscordRolePicker({
  value,
  onChange,
  roles = [],
  isLoading = false,
  isError = false,
  disabled = false,
}: DiscordRolePickerProps) {
  const optionsId = `discord-role-options-${useId().replaceAll(':', '')}`;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [manualRole, setManualRole] = useState('');

  const selectedIds = useMemo(() => [...new Set(value)], [value]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const rolesById = useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles]);
  const filteredRoles = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('ru-RU');
    if (!query) return roles;
    return roles.filter(
      (role) => role.name.toLocaleLowerCase('ru-RU').includes(query) || role.id.includes(query),
    );
  }, [roles, search]);

  function toggleRole(id: string) {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((roleId) => roleId !== id));
      return;
    }
    if (selectedIds.length >= MAX_DISCORD_ROLES) {
      toast.error(`Можно выбрать не более ${MAX_DISCORD_ROLES} ролей`);
      return;
    }
    onChange([...selectedIds, id]);
  }

  function addManualRoles() {
    const parsed = [...new Set(manualRole.split(/[\s,]+/).map((id) => id.trim()).filter(Boolean))];
    const valid = parsed.filter((id) => DISCORD_ROLE_ID.test(id));
    if (valid.length === 0) {
      toast.error('Укажите корректный ID роли Discord');
      return;
    }
    if (valid.length !== parsed.length) {
      toast.warning('Некорректные ID пропущены');
    }
    const next = [...new Set([...selectedIds, ...valid])];
    if (next.length > MAX_DISCORD_ROLES) {
      toast.error(`Можно выбрать не более ${MAX_DISCORD_ROLES} ролей`);
      return;
    }
    onChange(next);
    setManualRole('');
  }

  return (
    <div className="space-y-3">
      {roles.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-white/10 bg-eol-black/30">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={optionsId}
            disabled={disabled}
            onClick={() => setOpen((current) => !current)}
            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-eol-white">Выбрать роли из Discord</span>
              <span className="block truncate text-xs text-eol-desc">
                {selectedIds.length > 0 ? `Выбрано: ${selectedIds.length}` : `Доступно ролей: ${roles.length}`}
              </span>
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-eol-desc transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>

          {open && (
            <div id={optionsId} className="border-t border-white/10 p-2">
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-eol-desc" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Поиск по названию или ID"
                  className="h-9 pl-8"
                  disabled={disabled}
                />
              </div>
              <div className="max-h-52 space-y-1 overflow-y-auto pr-1">
                {filteredRoles.map((role) => {
                  const selected = selectedSet.has(role.id);
                  const canToggle = role.assignable || selected;
                  return (
                    <button
                      key={role.id}
                      type="button"
                      role="checkbox"
                      aria-checked={selected}
                      disabled={disabled || !canToggle}
                      onClick={() => toggleRole(role.id)}
                      title={!role.assignable && !selected ? 'Бот не может выдать эту роль' : undefined}
                      className={`flex w-full min-w-0 items-center gap-3 rounded-md border px-3 py-2 text-left transition ${
                        selected
                          ? 'border-eol-pink/50 bg-eol-pink/10'
                          : 'border-transparent hover:border-white/10 hover:bg-white/5'
                      } disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      <span
                        className="h-3 w-3 shrink-0 rounded-full border border-white/20"
                        style={{ backgroundColor: role.color ?? '#73767d' }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-eol-white">{role.name}</span>
                        <span className="block truncate font-mono text-[10px] text-eol-desc">{role.id}</span>
                      </span>
                      {!role.assignable && !selected && (
                        <span className="text-[10px] text-eol-desc">Недоступна</span>
                      )}
                      <span
                        aria-hidden="true"
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          selected ? 'border-eol-pink bg-eol-pink text-eol-black' : 'border-white/20'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3" />}
                      </span>
                    </button>
                  );
                })}
                {filteredRoles.length === 0 && (
                  <p className="px-3 py-5 text-center text-sm text-eol-desc">
                    Роли с таким названием или ID не найдены.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <p
          className={`rounded-lg border p-3 text-sm ${
            isError
              ? 'border-eol-red/20 bg-eol-red/5 text-eol-red'
              : 'border-white/5 bg-white/[0.02] text-eol-desc'
          }`}
        >
          {isLoading
            ? 'Получаем роли с Discord-сервера…'
            : isError
              ? 'Не удалось получить роли Discord. Сохранённые роли не изменены.'
              : 'На Discord-сервере нет доступных ролей.'}
        </p>
      )}

      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-eol-desc">
          Добавить роль по ID
        </p>
        <div className="flex gap-2 max-sm:flex-col">
          <Input
            value={manualRole}
            onChange={(event) => setManualRole(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addManualRoles();
              }
            }}
            placeholder="Например: 123456789012345678"
            className="font-mono"
            disabled={disabled}
          />
          <Button type="button" variant="secondary" onClick={addManualRoles} disabled={disabled}>
            <Plus className="h-4 w-4" /> Добавить
          </Button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="max-h-20 overflow-y-auto rounded-lg border border-white/5 bg-white/[0.02] p-2">
          <div className="flex flex-wrap gap-2">
            {selectedIds.map((id) => {
              const role = rolesById.get(id);
              return (
                <Badge
                  key={id}
                  variant={role ? 'default' : 'outline'}
                  className="gap-1 normal-case"
                  title={id}
                >
                  {role?.name ?? id}
                  <button
                    type="button"
                    onClick={() => toggleRole(id)}
                    aria-label={`Убрать роль ${role?.name ?? id}`}
                    disabled={disabled}
                    className="disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
